import {
  PaginationContent,
  PaginationItem,
  PaginationJumpFirst,
  PaginationJumpLast,
  PaginationLink,
  PaginationNextPageGroup,
  PaginationPreviousPageGroup,
  PaginationRoot
} from '@/shared/components/atoms/Pagination';
import { cn } from '@/styles/utils';

interface PaginationProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
  className?: string;
}

export function Pagination({
  currentPage,
  setCurrentPage,
  totalPages,
  className,
}: PaginationProps) {
  const pageNumbers = [];
  const VISIBLE_PAGES = 5; // 한 번에 보여줄 페이지 수

  // 현재 페이지가 속한 그룹의 시작과 끝 페이지 계산
  const currentGroup = Math.ceil(currentPage / VISIBLE_PAGES);
  const start = (currentGroup - 1) * VISIBLE_PAGES + 1;
  const end = Math.min(currentGroup * VISIBLE_PAGES, totalPages);

  // start부터 end까지의 페이지 번호 생성
  pageNumbers.push(
    ...Array.from({ length: end - start + 1 }, (_, i) => start + i),
  );

  // 이전 페이지 그룹으로 이동(5개 단위로 모듈러)
  // Ex) 현재 페이지가 13이면 6으로 이동
  const handlePreviousPageGroup = () => {
    const currentGroup = Math.ceil(currentPage / 5);
    const prevGroupStart = (currentGroup - 2) * VISIBLE_PAGES + 1;
    if (prevGroupStart >= 1) {
      setCurrentPage(prevGroupStart);
    }
  };

  // 다음 페이지 그룹으로 이동(5개 단위로 모듈러)
  // Ex) 현재 페이지가 13이면 16으로 이동
  const handleNextPageGroup = () => {
    const currentGroup = Math.ceil(currentPage / 5);
    const nextGroupStart = currentGroup * VISIBLE_PAGES + 1;
    if (nextGroupStart <= totalPages) {
      setCurrentPage(nextGroupStart);
    }
  };

  const handleJumpFirstPage = () => {
    setCurrentPage(1);
  };

  const handleJumpLastPage = () => {
    setCurrentPage(totalPages);
  };

  // 페이지 버튼 클릭 처리 함수
  const handlePageClick = (pageNumber: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPage !== pageNumber) {
      setCurrentPage(pageNumber);
    }
  };

  // 버튼 활성화 여부 확인
  const isFirstPageDisabled = currentPage === 1 || currentPage <= 5;
  const isPreviousPageDisabled = currentPage <= 5;
  const isNextPageDisabled = Math.ceil(currentPage / 5) >= Math.ceil(totalPages / 5);
  const isLastPageDisabled = currentPage === totalPages || Math.ceil(currentPage / 5) >= Math.ceil(totalPages / 5);

  return (
    <PaginationRoot className={cn(className)}>
      <PaginationContent>
        <PaginationItem>
          <PaginationJumpFirst
            onClick={(e) => {
              e.preventDefault();
              handleJumpFirstPage();
            }}
            className={cn(
              isFirstPageDisabled ? 'pointer-events-none' : 'cursor-pointer',
            )}
            isDisabled={isFirstPageDisabled}
          />
        </PaginationItem>

        <PaginationItem>
          <PaginationPreviousPageGroup
            onClick={(e) => {
              e.preventDefault();
              handlePreviousPageGroup();
            }}
            className={cn(
              isPreviousPageDisabled ? 'pointer-events-none' : 'cursor-pointer',
            )}
            isDisabled={isPreviousPageDisabled}
          />
        </PaginationItem>

        {pageNumbers.map((pageNumber) => (
          <PaginationItem key={pageNumber}>
            <PaginationLink
              isActive={currentPage === pageNumber}
              onClick={handlePageClick(pageNumber)}
              className={cn(
                currentPage === pageNumber
                  ? 'pointer-events-none'
                  : 'cursor-pointer',
              )}
            >
              {pageNumber}
            </PaginationLink>
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNextPageGroup
            onClick={(e) => {
              e.preventDefault();
              handleNextPageGroup();
            }}
            className={cn(
              isNextPageDisabled ? 'pointer-events-none' : 'cursor-pointer',
            )}
            isDisabled={isNextPageDisabled}
          />
        </PaginationItem>

        <PaginationItem>
          <PaginationJumpLast
            onClick={(e) => {
              e.preventDefault();
              handleJumpLastPage();
            }}
            className={cn(
              isLastPageDisabled ? 'pointer-events-none' : 'cursor-pointer',
            )}
            isDisabled={isLastPageDisabled}
          />
        </PaginationItem>
      </PaginationContent>
    </PaginationRoot>
  );
}
